import { permanentTagIdsMatch } from '../services/permanent-tag.service';

/**
 * Task work pools (`assignedToUserIds` / `assignedToTags`).
 *
 * Anyone in the pool may see and work the task. Read structurally: the API
 * wire is flat `string[]` while the proto model keeps `StringListContainer`
 * (`{ list: string[] }`), and consumers may still pin a classes version
 * without these fields.
 */

/** Flat wire array, proto container, or nothing. */
export type StringListLike =
  string[] | { list?: string[] | null } | null | undefined;

export interface TaskAssignmentFields {
  assignedToUserIds?: StringListLike;
  assignedToTags?: StringListLike;
}

export interface TaskAssignmentPool {
  userIds: string[];
  tags: string[];
}

function assignmentFields(
  task: object | null | undefined
): TaskAssignmentFields {
  return (task ?? {}) as TaskAssignmentFields;
}

export function readStringList(value: StringListLike): string[] {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (value && Array.isArray(value.list)) {
    return value.list.filter(Boolean);
  }
  return [];
}

export function taskAssignedUserIds(task: object | null | undefined): string[] {
  return readStringList(assignmentFields(task).assignedToUserIds);
}

export function taskAssignedTags(task: object | null | undefined): string[] {
  return readStringList(assignmentFields(task).assignedToTags);
}

export function taskAssignmentPool(
  task: object | null | undefined
): TaskAssignmentPool {
  return {
    userIds: taskAssignedUserIds(task),
    tags: taskAssignedTags(task),
  };
}

export function taskHasAssignment(task: object | null | undefined): boolean {
  return (
    taskAssignedUserIds(task).length > 0 || taskAssignedTags(task).length > 0
  );
}

/** Named on the pool, or a live member of an assigned tag. */
export function viewerInAssignmentPool(
  task: object | null | undefined,
  userId: string | undefined,
  userTags: readonly string[] | undefined
): boolean {
  const pool = taskAssignmentPool(task);
  if (userId && pool.userIds.includes(userId)) {
    return true;
  }
  if (!userTags?.length || pool.tags.length === 0) {
    return false;
  }
  return pool.tags.some(tag =>
    userTags.some(userTag => permanentTagIdsMatch(tag, userTag))
  );
}

/**
 * Write the pool back onto a parsed task in the proto container shape, so a
 * task read from the flat API wire matches what `Task.toJSON` would produce.
 * Empty lists are dropped rather than stored as empty containers.
 */
export function applyTaskAssignmentPool<T>(
  task: T,
  pool: TaskAssignmentPool
): T {
  const target = task as unknown as {
    assignedToUserIds?: { list: string[] };
    assignedToTags?: { list: string[] };
  };
  target.assignedToUserIds = pool.userIds.length
    ? { list: [...pool.userIds] }
    : undefined;
  target.assignedToTags = pool.tags.length
    ? { list: [...pool.tags] }
    : undefined;
  return task;
}

/** Default work pool stamped onto tasks created in a category. */
export function categoryDefaultAssignment(category: {
  defaultAssignedToUserIds?: StringListLike;
  defaultAssignedToTags?: StringListLike;
}): TaskAssignmentPool {
  return {
    userIds: readStringList(category?.defaultAssignedToUserIds),
    tags: readStringList(category?.defaultAssignedToTags),
  };
}

/** Structural shape of a work pool, independent of who is looking at it. */
export type TaskAssignmentOptics =
  'individual' | 'team_shared' | 'mixed' | 'unassigned';

export function assignmentPoolOptics(
  pool: TaskAssignmentPool
): TaskAssignmentOptics {
  const hasUsers = pool.userIds.length > 0;
  const hasTags = pool.tags.length > 0;
  if (hasUsers && hasTags) {
    return 'mixed';
  }
  if (hasTags) {
    return 'team_shared';
  }
  if (hasUsers) {
    return 'individual';
  }
  return 'unassigned';
}

export function taskAssignmentOptics(
  task: object | null | undefined
): TaskAssignmentOptics {
  return assignmentPoolOptics(taskAssignmentPool(task));
}
